import React from 'react';

// ============================================
// Halloween edition — temporary seasonal layer.
//
// Turns itself on for HALLOWEEN_WINDOW (UK dates) every year and off again
// afterwards, so nothing needs reverting once the season's over. Everything
// seasonal lives in this file; App.jsx only reads HALLOWEEN_ON and renders
// <HalloweenDecor />. To retire it for good, delete those references and this
// file.
//
// Preview / kill switch: ?halloween=1 forces it on, ?halloween=0 forces it off,
// ?halloween=auto goes back to the date window. The choice sticks per browser.
// ============================================

const HALLOWEEN_WINDOW = { from: [10, 17], to: [11, 2] }; // [month, day], inclusive

function inWindow() {
  try {
    const parts = new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/London', month: 'numeric', day: 'numeric' }).formatToParts(new Date());
    const m = Number(parts.find(p => p.type === 'month').value);
    const d = Number(parts.find(p => p.type === 'day').value);
    const key = m * 100 + d;
    const from = HALLOWEEN_WINDOW.from[0] * 100 + HALLOWEEN_WINDOW.from[1];
    const to = HALLOWEEN_WINDOW.to[0] * 100 + HALLOWEEN_WINDOW.to[1];
    return from <= to ? key >= from && key <= to : key >= from || key <= to;
  } catch { return false; }
}

function resolve() {
  if (typeof window === 'undefined') return false;
  let pref = null;
  try {
    const q = new URLSearchParams(window.location.search).get('halloween');
    if (q === '1' || q === '0') localStorage.setItem('snutzy_halloween', q);
    else if (q === 'auto') localStorage.removeItem('snutzy_halloween');
    pref = localStorage.getItem('snutzy_halloween');
  } catch { /* storage blocked — fall through to the date window */ }
  if (pref === '1') return true;
  if (pref === '0') return false;
  return inWindow();
}

export const HALLOWEEN_ON = resolve();

// ---------- Slot filter ----------
// Title keywords for the "Halloween slots only" picker toggle. Whole words only,
// so "Hellas" / "Nightclub" style names don't sneak in. SPOOKY_EXCLUDE drops
// titles that hit a keyword but aren't spooky (chilli slots, pirates, Greece…).
const SPOOKY_RE = /\b(hallowe?en|spooky|spooks?|ghosts?|ghostly|ghouls?|zombies?|vampires?|vampire's|dracula|transylvania|undead|dead|deadly|death|haunt\w*|graves?|graveyard|tombstone|crypts?|skulls?|skeletons?|witch\w*|demons?|demonic|devils?|devil's|hell|hellfire|hellcatraz|pumpkins?|monsters?|frankenslot's|werewol\w*|mumm(?:y|ies)|mummy's|frankenstein|horror|nightmares?|scary|evil|dark|darkness|blood|bloody|bloodthirst|reaper|souls?|curse[ds]?|banshee|phantoms?|coffins?|creep\w*|wicked|hex|voodoo|necro\w*|fright\w*|macabre|occult|exorcis\w*|possessed|muertos|catrina|sinister|gothic|grim|chainsaw|asylum|cemetery|morgue|cauldron|trick or treat|fangs?|fang's|cthulhu|underworld|rotten|corpse|infectious|disturbed|spider|break bones|mental)\b/i;
const SPOOKY_EXCLUDE = /\bhell hot\b|night in paris|blood diamond|curse of (?:ra|medusa)|\bdead man|first blood|hot to burn|gladius|ultra dark|deadly 5|killer sharks/i;

export const isSpookySlot = (slot) => !!slot?.name && SPOOKY_RE.test(slot.name) && !SPOOKY_EXCLUDE.test(slot.name);

// ---------- Decor ----------
// Deterministic pseudo-random so the layout doesn't reshuffle on re-render.
const rand = (i, salt) => { const x = Math.sin(i * 97.13 + salt * 13.7) * 43758.5453; return x - Math.floor(x); };

const EMBERS = Array.from({ length: 18 }, (_, i) => ({
  left: `${(rand(i, 1) * 100).toFixed(1)}%`,
  size: 2 + Math.round(rand(i, 2) * 3),
  dur: 16 + Math.round(rand(i, 3) * 16),
  delay: -Math.round(rand(i, 4) * 30),
  drift: `${Math.round((rand(i, 5) - 0.5) * 120)}px`,
  purple: i % 3 === 0,
}));

// Bats fly across now and then — the flight is the first ~third of each loop,
// the rest of the loop they're off-screen, so the sky isn't constantly busy.
const BATS = [
  { top: '16vh', size: 34, dur: 38, delay: 3, dir: 1 },
  { top: '24vh', size: 22, dur: 38, delay: 4.2, dir: 1 },
  { top: '11vh', size: 26, dur: 46, delay: 21, dir: -1 },
  { top: '38vh', size: 18, dur: 52, delay: 34, dir: 1 },
];

const BAT_PATH = 'M32 11c-.6-1.7-1.3-3-2.2-4.2l-.3 3.6c-3.8-2.9-10.6-4.6-19.5-3.4 3 1.6 4.4 4.3 4 7.2-3.4-1.2-7.8-1-12 1.2 5 1.4 8.2 4.4 9.4 8.6 2.8-2.6 6.6-3.2 10-1.6 1.6-2.6 4.4-3.4 7-2.2L32 26l3.6-5.8c2.6-1.2 5.4-.4 7 2.2 3.4-1.6 7.2-1 10 1.6 1.2-4.2 4.4-7.2 9.4-8.6-4.2-2.2-8.6-2.4-12-1.2-.4-2.9 1-5.6 4-7.2-8.9-1.2-15.7.5-19.5 3.4l-.3-3.6c-.9 1.2-1.6 2.5-2.2 4.2z';

const CSS = `
  /* Swap the bottom-right purple aura for a low pumpkin glow; the top-left
     glow is untouched so it still lines up with .nav / .sidebar. */
  .app-container::before {
    background:
      radial-gradient(ellipse 55vw 60vh at 20vw 0px, rgba(168, 85, 247, 0.13) 0%, rgba(168, 85, 247, 0.07) 28%, rgba(168, 85, 247, 0.035) 48%, rgba(168, 85, 247, 0.015) 66%, rgba(168, 85, 247, 0.005) 83%, transparent 100%),
      radial-gradient(ellipse 70vw 45vh at 70% 105%, rgba(249, 115, 22, 0.12) 0%, rgba(249, 115, 22, 0.06) 25%, rgba(249, 115, 22, 0.025) 45%, rgba(249, 115, 22, 0.008) 62%, transparent 78%),
      radial-gradient(ellipse at 15% 100%, rgba(168, 85, 247, 0.07) 0%, rgba(168, 85, 247, 0.03) 25%, transparent 55%) !important;
  }
  .nav { border-bottom-color: rgba(249, 115, 22, 0.22) !important; }
  .logo { position: relative; }
  .logo-img { border-color: rgba(249, 115, 22, 0.55) !important; }
  .logo:hover .logo-img { border-color: #f97316 !important; box-shadow: 0 0 18px rgba(249, 115, 22, 0.45) !important; }
  .logo::after {
    content: '🎃'; position: absolute; left: 32px; top: 22px;
    font-size: 14px; line-height: 1; pointer-events: none;
    filter: drop-shadow(0 0 4px rgba(249, 115, 22, 0.8));
    animation: hw-bob 3.2s ease-in-out infinite;
  }

  .hw-layer { position: fixed; inset: 0; pointer-events: none; overflow: hidden; }
  .hw-back { z-index: 0; }
  .hw-front { z-index: 98; }

  /* Crescent: an empty disc whose inset shadow is the lit sliver, plus a halo. */
  .hw-moon {
    position: absolute; right: 9vw; top: 15vh; width: 130px; height: 130px; border-radius: 50%;
    box-shadow: inset -20px 8px 0 0 #fed7aa;
    filter: drop-shadow(0 0 14px rgba(251, 146, 60, 0.75)) drop-shadow(0 0 50px rgba(249, 115, 22, 0.45));
    transform: rotate(-18deg);
    opacity: 0.55;
  }
  .hw-moon::after {
    content: ''; position: absolute; inset: -90px; border-radius: 50%;
    background: radial-gradient(circle, rgba(249, 115, 22, 0.10) 0%, rgba(249, 115, 22, 0.04) 35%, transparent 65%);
  }

  .hw-fog {
    position: absolute; left: -50%; bottom: -8vh; width: 200%; height: 34vh;
    background:
      radial-gradient(ellipse 18% 55% at 10% 70%, rgba(216, 180, 254, 0.16), transparent 70%),
      radial-gradient(ellipse 22% 50% at 32% 80%, rgba(255, 255, 255, 0.10), transparent 70%),
      radial-gradient(ellipse 16% 60% at 55% 75%, rgba(216, 180, 254, 0.13), transparent 70%),
      radial-gradient(ellipse 24% 45% at 78% 85%, rgba(255, 255, 255, 0.09), transparent 70%),
      radial-gradient(ellipse 18% 55% at 95% 70%, rgba(216, 180, 254, 0.14), transparent 70%);
    filter: blur(18px);
    animation: hw-fog 70s linear infinite;
  }
  .hw-fog-2 { bottom: -14vh; height: 28vh; opacity: 0.7; animation-duration: 110s; animation-direction: reverse; }

  .hw-ember {
    position: absolute; bottom: -10px; border-radius: 50%;
    background: #fb923c; box-shadow: 0 0 8px 2px rgba(249, 115, 22, 0.7);
    opacity: 0; animation: hw-rise linear infinite;
  }
  .hw-ember.purple { background: #c084fc; box-shadow: 0 0 8px 2px rgba(168, 85, 247, 0.7); }

  .hw-bat-path { position: absolute; left: 0; animation: hw-fly linear infinite; opacity: 0; }
  .hw-bat-path.rtl { animation-name: hw-fly-rtl; }
  .hw-bat-path svg { display: block; fill: #0b0610; filter: drop-shadow(0 0 6px rgba(249, 115, 22, 0.35)); animation: hw-flap 0.22s ease-in-out infinite alternate; transform-origin: 50% 70%; }

  .hw-web { position: absolute; right: 0; width: 150px; height: 150px; opacity: 0.18; }

  @keyframes hw-bob { 0%, 100% { transform: translateY(0) rotate(-6deg); } 50% { transform: translateY(-2px) rotate(6deg); } }
  @keyframes hw-fog { from { transform: translateX(0); } to { transform: translateX(25%); } }
  @keyframes hw-rise {
    0% { transform: translate(0, 0); opacity: 0; }
    10% { opacity: 0.55; }
    80% { opacity: 0.35; }
    100% { transform: translate(var(--drift), -105vh); opacity: 0; }
  }
  @keyframes hw-fly {
    0% { transform: translate(-12vw, 0); opacity: 1; }
    8% { transform: translate(18vw, -3vh); }
    16% { transform: translate(48vw, 2vh); }
    24% { transform: translate(78vw, -2vh); }
    32% { transform: translate(112vw, -6vh); opacity: 1; }
    32.1%, 100% { transform: translate(112vw, -6vh); opacity: 0; }
  }
  @keyframes hw-fly-rtl {
    0% { transform: translate(112vw, 0) scaleX(-1); opacity: 1; }
    8% { transform: translate(80vw, 3vh) scaleX(-1); }
    16% { transform: translate(50vw, -2vh) scaleX(-1); }
    24% { transform: translate(20vw, 2vh) scaleX(-1); }
    32% { transform: translate(-12vw, -5vh) scaleX(-1); opacity: 1; }
    32.1%, 100% { transform: translate(-12vw, -5vh) scaleX(-1); opacity: 0; }
  }
  @keyframes hw-flap { from { transform: scaleY(1); } to { transform: scaleY(0.45); } }

  @media (max-width: 900px) {
    .hw-web { display: none; }
    .hw-moon { width: 80px; height: 80px; right: 6vw; top: 12vh; box-shadow: inset -13px 5px 0 0 #fed7aa; }
  }
  @media (max-width: 700px) {
    .hw-moon { display: none; }
  }
  @media (prefers-reduced-motion: reduce) {
    .hw-fog, .hw-ember, .hw-bat-path, .hw-bat-path svg, .logo::after { animation: none !important; }
    .hw-ember, .hw-bat-path { display: none; }
  }
`;

// Corner cobweb: radial spokes + sagging rings, drawn from the top-right corner.
function Cobweb({ top }) {
  const spokes = [0, 18, 36, 54, 72, 90];
  const pt = (deg, r) => [150 - r * Math.cos((deg * Math.PI) / 180), r * Math.sin((deg * Math.PI) / 180)];
  const rings = [34, 62, 92, 124];
  return (
    <svg className="hw-web" style={{ top }} viewBox="0 0 150 150" fill="none" stroke="#e9d5ff" strokeWidth="0.9" strokeLinecap="round">
      {spokes.map(a => { const [x, y] = pt(a, 160); return <line key={a} x1="150" y1="0" x2={x} y2={y} />; })}
      {rings.map(r => (
        <path key={r} d={spokes.slice(1).map((a, i) => {
          const [x0, y0] = pt(spokes[i], r);
          const [x1, y1] = pt(a, r);
          const [mx, my] = pt((spokes[i] + a) / 2, r * 0.86);
          return `${i === 0 ? `M${x0} ${y0}` : ''} Q${mx} ${my} ${x1} ${y1}`;
        }).join(' ')} />
      ))}
      <line x1="118" y1="0" x2="118" y2="58" strokeWidth="0.6" />
      <circle cx="118" cy="62" r="4" fill="#e9d5ff" stroke="none" />
    </svg>
  );
}

export function HalloweenDecor({ navHeight = 60 }) {
  return (
    <>
      <style>{CSS}</style>
      <div className="hw-layer hw-back" aria-hidden="true">
        <div className="hw-moon" />
        <div className="hw-fog" />
        <div className="hw-fog hw-fog-2" />
        {EMBERS.map((e, i) => (
          <span
            key={i}
            className={`hw-ember${e.purple ? ' purple' : ''}`}
            style={{ left: e.left, width: e.size, height: e.size, animationDuration: `${e.dur}s`, animationDelay: `${e.delay}s`, '--drift': e.drift }}
          />
        ))}
      </div>
      <div className="hw-layer hw-front" aria-hidden="true">
        {BATS.map((b, i) => (
          <div
            key={i}
            className={`hw-bat-path${b.dir < 0 ? ' rtl' : ''}`}
            style={{ top: b.top, animationDuration: `${b.dur}s`, animationDelay: `${b.delay}s` }}
          >
            <svg width={b.size} height={b.size / 2} viewBox="0 0 64 32" style={{ animationDuration: `${0.18 + i * 0.03}s` }}>
              <path d={BAT_PATH} />
            </svg>
          </div>
        ))}
        <Cobweb top={navHeight} />
      </div>
    </>
  );
}

export default HalloweenDecor;
